/**
 * The local player's body and how it moves (spec 2m §2), and the pure geometry the walker runs on (§3.1).
 * Triangles are world-space Float32Arrays, 9 floats each; polygons are cross-sections of a horizontal slab of
 * them, projected onto the x-z plane.
 */
export const BODY_RADIUS = 0.35;
/** Top of the collision band and the avatar's height. */
export const BODY_HEIGHT = 1.8;
/** The camera's height above the feet. */
export const EYE = 1.6;
/** Bottom of the collision band: ledges up to this are stepped onto. */
export const STEP_UP = 0.25;
/** Walking off a drop up to this keeps the feet on the ground instead of falling. */
export const SNAP_DOWN = 0.2;
export const GRAVITY = 15; // m/s², tuned for a game-like fall
/** How high a jump lifts the feet: with the band starting at STEP_UP it clears a 1.1 m railing, which most games let people hop (owner feedback 2026-10-12). */
export const JUMP_APEX = 1.0;
export const JUMP_SPEED = Math.sqrt(2 * GRAVITY * JUMP_APEX);
/** Longest horizontal step between pushes, under half the radius, so a centre never crosses a wall. */
export const MOVE_SUBSTEP = 0.15;
// what the guide tells agents (build/collision), derived from the numbers above
export const JUMP_ONTO = JUMP_APEX + STEP_UP;
/** Platforms meant to be jumped between: under JUMP_ONTO by a margin, so a landing never hangs on the edge. */
export const PLATFORM_GAP = 1.1;
/** A wall that is meant to keep people out: JUMP_ONTO plus a margin. Railings, fences and balcony edges are lower and are jumped over. */
export const BARRIER_MIN = 1.4;
export const LINTEL_MIN = 2.0;
/** atan(STEP_UP / BODY_RADIUS) ≈ 35.5° is the steepest ramp the band clears; the guide promises 30°. */
export const RAMP_MAX_DEG = 30;
/** The legs: both of Guava Bot's legs stay within this of the centre line from the feet to the hips (spec 2m-2 §2). */
export const LEG_RADIUS = 0.2;
/** Hip height above the feet: the band below it is LEG_RADIUS wide, the band above it BODY_RADIUS. */
export const LEGS_TOP = 0.9;
/** What the guide asks of a stair tread: a tread deeper than LEG_RADIUS is climbed, with 0.05 m to spare. */
export const TREAD_MIN = 0.25;
/** The smallest normal.y a face can have and still lift the feet: RAMP_MAX_DEG, plus 0.5° for Float32 triangles. */
export const WALKABLE_NY = Math.cos(((RAMP_MAX_DEG + 0.5) * Math.PI) / 180);

/** Resting contact is not a trap: the walker leaves a body exactly BODY_RADIUS from a wall. */
const CONTACT = 0.01;
const PUSH_ROUNDS = 8;
/** Cuts shallower than this are resting contact. */
const PEN_TOL = 1e-4;

export interface Box2 { minX: number; minZ: number; maxX: number; maxZ: number }
/** A convex polygon on the x-z plane as x, z pairs, two points or more (a vertical face gives a segment). */
export type Poly2 = Float32Array;

/** Each triangle's part between heights lo and hi, projected onto x-z; triangles outside the box are skipped. */
export function slabPolygons(tris: Float32Array, lo: number, hi: number, box: Box2): Poly2[] {
  const out: Poly2[] = [];
  for (let t = 0; t + 8 < tris.length; t += 9) {
    const ay = tris[t + 1]!, by = tris[t + 4]!, cy = tris[t + 7]!;
    if (Math.max(ay, by, cy) < lo || Math.min(ay, by, cy) > hi) continue;
    const ax = tris[t]!, bx = tris[t + 3]!, cx = tris[t + 6]!, az = tris[t + 2]!, bz = tris[t + 5]!, cz = tris[t + 8]!;
    if (Math.max(ax, bx, cx) < box.minX || Math.min(ax, bx, cx) > box.maxX) continue;
    if (Math.max(az, bz, cz) < box.minZ || Math.min(az, bz, cz) > box.maxZ) continue;
    const clipped = clipY(clipY([ax, ay, az, bx, by, bz, cx, cy, cz], lo, 1), hi, -1);
    if (clipped.length < 6) continue;
    const p = new Float32Array((clipped.length / 3) * 2);
    for (let i = 0, j = 0; i < clipped.length; i += 3, j += 2) { p[j] = clipped[i]!; p[j + 1] = clipped[i + 2]!; }
    out.push(p);
  }
  return out;
}

/** Sutherland–Hodgman against the plane y = k, keeping the side where sign·(y − k) ≥ 0. */
function clipY(poly: number[], k: number, sign: 1 | -1): number[] {
  const out: number[] = [];
  const n = poly.length / 3;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = poly[i * 3]!, ay = poly[i * 3 + 1]!, az = poly[i * 3 + 2]!;
    const bx = poly[j * 3]!, by = poly[j * 3 + 1]!, bz = poly[j * 3 + 2]!;
    const da = sign * (ay - k), db = sign * (by - k);
    if (da >= 0) out.push(ax, ay, az);
    if ((da >= 0) !== (db >= 0)) { const s = da / (da - db); out.push(ax + s * (bx - ax), k, az + s * (bz - az)); }
  }
  return out;
}

interface Nearest { x: number; z: number; d2: number; inside: boolean }

/** The polygon's point nearest (x, z), and whether (x, z) lies inside it (only an area can hold a point). */
function nearest(p: Poly2, x: number, z: number): Nearest {
  const n = p.length / 2;
  let bx = p[0]!, bz = p[1]!, best = Infinity, pos = 0, neg = 0, area = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const ax = p[i * 2]!, az = p[i * 2 + 1]!, ex = p[j * 2]! - ax, ez = p[j * 2 + 1]! - az;
    const l2 = ex * ex + ez * ez;
    const s = l2 > 0 ? Math.min(1, Math.max(0, ((x - ax) * ex + (z - az) * ez) / l2)) : 0;
    const qx = ax + s * ex, qz = az + s * ez, d2 = (x - qx) ** 2 + (z - qz) ** 2;
    if (d2 < best) { best = d2; bx = qx; bz = qz; }
    const cross = ex * (z - az) - ez * (x - ax);
    if (cross > 1e-12) pos++; else if (cross < -1e-12) neg++;
    area += ax * p[j * 2 + 1]! - p[j * 2]! * az;
  }
  return { x: bx, z: bz, d2: best, inside: n >= 3 && Math.abs(area) > 1e-6 && (pos === n || neg === n) };
}

/** One horizontal band of the body: its radius and the polygons cut from the world at its height. */
export interface Band { r: number; polys: readonly Poly2[] }

/** How deep the body cuts into the deepest polygon of any band: r − distance, or r + distance for a centre inside an area. */
function penetration(bands: readonly Band[], x: number, z: number): number {
  let worst = 0;
  for (const { r, polys } of bands) for (const p of polys) {
    const q = nearest(p, x, z), d = Math.sqrt(q.d2);
    worst = Math.max(worst, q.inside ? r + d : r - d);
  }
  return worst;
}

/**
 * Move the body by (dx, dz) among the polygons of its bands: in steps of at most MOVE_SUBSTEP, each followed
 * by pushes, deepest cut first across all bands, each push along that band's radius, that leave the body exactly r
 * from anything it cut into. A centre inside an area leaves through the nearest edge. Sliding along walls and
 * settling in corners come from the pushes alone. When the pushes cannot clear every contact (a gap narrower than
 * the body: two corners pushing against each other) and the step made things no better, the step is undone, so
 * the body never creeps through.
 */
export function moveBody(x: number, z: number, dx: number, dz: number, bands: readonly Band[]): { x: number; z: number } {
  const len = Math.hypot(dx, dz);
  const steps = Math.max(1, Math.ceil(len / MOVE_SUBSTEP));
  for (let s = 0; s < steps; s++) {
    const fromX = x, fromZ = z, before = penetration(bands, x, z);
    x += dx / steps; z += dz / steps;
    for (let round = 0; round < PUSH_ROUNDS; round++) {
      // one push a round, out of the deepest cut: a wall cut into short pieces then pushes along its normal,
      // where pushing out of each piece in turn would slide the body sideways off the pieces' ends
      let deepest: Nearest | null = null, worst = 0, r = 0;
      for (const band of bands) for (const p of band.polys) {
        const q = nearest(p, x, z);
        if (!q.inside && q.d2 >= band.r * band.r - 1e-9) continue;
        const pen = q.inside ? band.r + Math.sqrt(q.d2) : band.r - Math.sqrt(q.d2);
        if (pen > worst) { worst = pen; deepest = q; r = band.r; }
      }
      if (!deepest) break;
      let nx = x - deepest.x, nz = z - deepest.z, d = Math.hypot(nx, nz);
      if (deepest.inside) { nx = -nx; nz = -nz; }
      if (d < 1e-9) { // centre exactly on the edge: back the way it came
        nx = len > 0 ? -dx / len : 1; nz = len > 0 ? -dz / len : 0; d = 1;
      }
      x = deepest.x + (nx / d) * r; z = deepest.z + (nz / d) * r;
    }
    const after = penetration(bands, x, z);
    if (after > PEN_TOL && after >= before - PEN_TOL) { x = fromX; z = fromZ; }
  }
  return { x, z };
}

/** A body of one band: a circle of radius r. */
export function moveCircle(x: number, z: number, dx: number, dz: number, r: number, polys: readonly Poly2[]): { x: number; z: number } {
  return moveBody(x, z, dx, dz, [{ r, polys }]);
}

/**
 * Height of the highest face straight under (x, z) between bottom and top; vertical faces do not count, and a face
 * steeper than RAMP_MAX_DEG (normal.y below WALKABLE_NY) counts only up to steepUpTo, so a slope never lifts the
 * feet but still catches them falling or walking down (spec 2m-2 §4.1). A steep face that rises less than STEP_UP
 * within itself is a chamfer or a rock's side, not a slope: it counts like any other face.
 */
export function groundUnder(tris: Float32Array, x: number, z: number, bottom: number, top: number, steepUpTo = Infinity): number | null {
  let best: number | null = null;
  for (let t = 0; t + 8 < tris.length; t += 9) {
    const ay = tris[t + 1]!, by = tris[t + 4]!, cy = tris[t + 7]!;
    if (Math.min(ay, by, cy) > top || Math.max(ay, by, cy) < bottom) continue;
    const ax = tris[t]!, az = tris[t + 2]!;
    const e1x = tris[t + 3]! - ax, e1z = tris[t + 5]! - az, e2x = tris[t + 6]! - ax, e2z = tris[t + 8]! - az;
    const det = e1x * e2z - e2x * e1z; // minus the y part of e1 × e2: twice the face's area seen from above
    if (Math.abs(det) < 1e-9) continue;
    const px = x - ax, pz = z - az;
    const u = (px * e2z - e2x * pz) / det, v = (e1x * pz - e1z * px) / det;
    if (u < -1e-6 || v < -1e-6 || u + v > 1 + 1e-6) continue;
    const y = ay + u * (by - ay) + v * (cy - ay);
    if (y < bottom || y > top || (best !== null && y <= best)) continue;
    if (y > steepUpTo && Math.max(ay, by, cy) - Math.min(ay, by, cy) > STEP_UP) {
      const e1y = by - ay, e2y = cy - ay;
      const cx = e1y * e2z - e1z * e2y, cz = e1x * e2y - e1y * e2x;
      if (Math.abs(det) < WALKABLE_NY * Math.hypot(cx, det, cz)) continue;
    }
    best = y;
  }
  return best;
}

/** Does any polygon come within r of (x, z), or hold it? */
export function circleHits(polys: readonly Poly2[], x: number, z: number, r: number): boolean {
  return polys.some((p) => { const q = nearest(p, x, z); return q.inside || q.d2 < r * r; });
}

/**
 * Is a player standing at feet caught by these triangles? Caught means the body cuts into a surface (resting
 * contact, one radius away, is not), or the eyes are inside solid material: a ray from them crosses an odd
 * number of triangles. Inside a hollow shape such as a house, whose walls are two faces, the body is free.
 */
export function bodyCaught(feet: { x: number; y: number; z: number }, tris: Float32Array): boolean {
  const r = BODY_RADIUS;
  const box = { minX: feet.x - r, minZ: feet.z - r, maxX: feet.x + r, maxZ: feet.z + r };
  const legs = slabPolygons(tris, feet.y + STEP_UP, feet.y + LEGS_TOP, box);
  const body = slabPolygons(tris, feet.y + LEGS_TOP, feet.y + BODY_HEIGHT, box);
  return circleHits(legs, feet.x, feet.z, LEG_RADIUS - CONTACT) || circleHits(body, feet.x, feet.z, r - CONTACT)
    || crossings(feet.x, feet.y + EYE, feet.z, tris) % 2 === 1;
}

// skewed so the ray does not run along the edges and faces of axis-aligned models
const RAY = (() => { const d = [0.5773, 0.0731, 0.8131], l = Math.hypot(...d); return d.map((v) => v / l) as [number, number, number]; })();

/** How many triangles the ray from (ox, oy, oz) along RAY crosses (Möller–Trumbore). */
function crossings(ox: number, oy: number, oz: number, tris: Float32Array): number {
  const [dx, dy, dz] = RAY;
  let n = 0;
  for (let t = 0; t < tris.length; t += 9) {
    const ax = tris[t]!, ay = tris[t + 1]!, az = tris[t + 2]!;
    const e1x = tris[t + 3]! - ax, e1y = tris[t + 4]! - ay, e1z = tris[t + 5]! - az;
    const e2x = tris[t + 6]! - ax, e2y = tris[t + 7]! - ay, e2z = tris[t + 8]! - az;
    const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
    const det = e1x * px + e1y * py + e1z * pz;
    if (Math.abs(det) < 1e-12) continue;
    const inv = 1 / det, sx = ox - ax, sy = oy - ay, sz = oz - az;
    const u = (sx * px + sy * py + sz * pz) * inv;
    if (u < 0 || u > 1) continue;
    const qx = sy * e1z - sz * e1y, qy = sz * e1x - sx * e1z, qz = sx * e1y - sy * e1x;
    const v = (dx * qx + dy * qy + dz * qz) * inv;
    if (v < 0 || u + v > 1) continue;
    if ((e2x * qx + e2y * qy + e2z * qz) * inv > 0) n++;
  }
  return n;
}
